import type { Axis } from 'highcharts/highcharts.src';
import moment from 'moment/moment';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    chartColors,
    CLIENT_OUTPUT_AT_PROJECTION_END_TEXT,
    CLIENT_OUTPUT_AT_PROJECTION_START_TEXT,
    CLIENT_OUTPUT_AT_PROJECTION_START_VALUE_TEXT,
    CLIENT_OUTPUT_STARTING_DOT_CIRCLE,
    FONT_FAMILY_VERLAG,
    FONT_WEIGHT_REGULAR,
} from '../../config/constants';
import { formatNumberToCompactNotation } from '../../utils/renderUtils';

const atProjectionStartStyle = {
    fontSize: '1.75rem',
    color: chartColors.LABEL_CLIENT_OUTPUT,
    fontFamily: FONT_FAMILY_VERLAG,
};

interface IProps {
    totalSelectedBankableAssets: number;
    projectionPeriodYears: number;
}

export const useClientOutputChartCallbacks = ({ totalSelectedBankableAssets, projectionPeriodYears }: IProps) => {
    const { t } = useTranslation();

    const addAtProjectionStartText = useCallback(
        (chart: Highcharts.Chart, xAxisBox: any) => {
            const atProjectionStartText = `${t('atProjectionStart')}`;

            // @ts-expect-error  Problems with missing Highcharts types
            if (chart[CLIENT_OUTPUT_AT_PROJECTION_START_TEXT]) {
                // @ts-expect-error  Problems with missing Highcharts types
                chart[CLIENT_OUTPUT_AT_PROJECTION_START_TEXT].destroy();
            }
            // @ts-expect-error  Problems with missing Highcharts types
            chart[CLIENT_OUTPUT_AT_PROJECTION_START_TEXT] = chart.renderer
                // @ts-expect-error  Problems with missing Highcharts types
                .text(atProjectionStartText, chart['plotBox'].x, chart.plotHeight + xAxisBox.y - 10)
                .css({
                    ...atProjectionStartStyle,
                    fontWeight: 'bold',
                })
                .add()
                .toFront();
        },
        [t],
    );

    const addAtProjectionStartValue = useCallback(
        (chart: Highcharts.Chart, xAxisBox: any) => {
            // @ts-expect-error  Problems with missing Highcharts types
            if (chart[CLIENT_OUTPUT_AT_PROJECTION_START_VALUE_TEXT]) {
                // @ts-expect-error  Problems with missing Highcharts types
                chart[CLIENT_OUTPUT_AT_PROJECTION_START_VALUE_TEXT].destroy();
            }
            // @ts-expect-error  Problems with missing Highcharts types
            chart[CLIENT_OUTPUT_AT_PROJECTION_START_VALUE_TEXT] = chart.renderer
                .text(
                    `${formatNumberToCompactNotation(totalSelectedBankableAssets)}`,
                    // @ts-expect-error  Problems with missing Highcharts types
                    chart['plotBox'].x + chart[CLIENT_OUTPUT_AT_PROJECTION_START_TEXT].getBBox().width + 50,
                    chart.plotHeight + xAxisBox.y - 10,
                )
                .css(atProjectionStartStyle)
                .add()
                .toFront();
        },
        [totalSelectedBankableAssets],
    );

    const addAtProjectionStartAmount = useCallback(
        (chart: Highcharts.Chart) => {
            const xAxis = chart.xAxis[0] as Axis;
            // @ts-expect-error  Problems with missing Highcharts types
            const xAxisBox = xAxis['gridGroup'].getBBox();
            addAtProjectionStartText(chart, xAxisBox);
            addAtProjectionStartValue(chart, xAxisBox);
        },
        [addAtProjectionStartText, addAtProjectionStartValue],
    );

    const addAtProjectEndText = useCallback(
        (chart: Highcharts.Chart) => {
            const projectionEndDate = moment().add(projectionPeriodYears, 'years').year();
            const legendBox = chart.legend.box.getBBox();
            // @ts-expect-error  Problems with missing Highcharts types
            if (chart[CLIENT_OUTPUT_AT_PROJECTION_END_TEXT]) {
                // @ts-expect-error  Problems with missing Highcharts types
                chart[CLIENT_OUTPUT_AT_PROJECTION_END_TEXT].destroy();
            }
            // @ts-expect-error  Problems with missing Highcharts types
            chart[CLIENT_OUTPUT_AT_PROJECTION_END_TEXT] = chart.renderer
                // @ts-expect-error  Problems with missing Highcharts types
                .text(`${t('atProjectionEnd')} - ${projectionEndDate}`, 0, chart.legend.group['alignAttr'].translateY + legendBox.height / 2 + 15)
                .css({
                    fontWeight: `${FONT_WEIGHT_REGULAR}`,
                    fontFamily: FONT_FAMILY_VERLAG,
                    fontStyle: 'italic',
                    fontSize: '2rem',
                    color: chartColors.LABEL_CLIENT_OUTPUT,
                })
                .add();
        },
        [t, projectionPeriodYears],
    );

    const addCircleMarkerAtGraphStart = useCallback((chart: Highcharts.Chart) => {
        // Destroy existing circle if it exists
        // @ts-expect-error  Problems with missing Highcharts types
        if (chart[CLIENT_OUTPUT_STARTING_DOT_CIRCLE]) {
            // @ts-expect-error  Problems with missing Highcharts types
            chart[CLIENT_OUTPUT_STARTING_DOT_CIRCLE].destroy();
        }

        // Find the first line/spline series with data
        const firstSeries = chart.series.find((series) => {
            return (series.type === 'line' || series.type === 'spline') && series.visible && series.points && series.points.length > 0;
        });

        if (!firstSeries || !firstSeries.points || firstSeries.points.length === 0) return;

        // Check if plotX and plotY are available
        const firstPoint = firstSeries.points[0];
        if (firstPoint.plotX === undefined || firstPoint.plotY === undefined) return;

        // Get plotBox to account for chart margins and offsets
        // @ts-expect-error  Problems with missing Highcharts types
        const plotBox = chart['plotBox'];
        if (!plotBox) return;

        // Calculate absolute position: plotBox position + point's plot coordinates
        const x = plotBox.x + firstPoint.plotX;
        const y = plotBox.y + firstPoint.plotY;

        // @ts-expect-error  Problems with missing Highcharts types
        chart[CLIENT_OUTPUT_STARTING_DOT_CIRCLE] = chart.renderer.circle(x, y, 10).attr({ fill: '#000000', zIndex: 10 }).add().toFront();
    }, []);

    return {
        addAtProjectionStartTextForClientOutput: addAtProjectionStartAmount,
        addAtProjectionEndTextForClientOutput: addAtProjectEndText,
        addCircleMarkerAtGraphStart,
    };
};
